import { TabsHeader } from 'platform/components';
import { useState } from 'react';
import { isNotNil } from 'ramda';
import { useColumnMenuContext } from '../../../../../../context/ColumnMenuContext';
import type { EagColumn } from '../../../../../../types/EagColumn';
import { ColumnMenuFilterTab } from './components/ColumnMenuFilterTab/ColumnMenuFilterTab';
import { ColumnMenuOptionsTab } from './components/ColumnMenuOptionsTab/ColumnMenuOptionsTab';
type ActiveTab = 'filter' | 'options';
type ColumnHeaderMenuProps = {
  column: EagColumn;
  columnPinned: 'LEFT' | 'RIGHT' | null | undefined;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Column header dropdown menu content with two icon tabs (filter + options).
 *
 * ## Tabs — Google-style
 * - Filter tab (funnel icon): renders the column's filter widget inline
 * - Options tab (menu icon): pin / autosize / reset actions
 * - Active tab has a blue underline indicator
 * - When the column has no filter definition, only the options tab is shown (no tab bar)
 *
 * ## Positioning
 * - Rendered as children of <Dropdown> from platform/components (in HeaderCell).
 * - Dropdown handles portal, positioning, focus management and dismiss.
 *
 *
 * ## State / callbacks
 * - Filter state and all action callbacks are read from ColumnMenuContext (no prop drilling)
 */
export function ColumnHeaderMenu(props: ColumnHeaderMenuProps) {
  const columnMenu = useColumnMenuContext();
  const hasFilter = isNotNil(props.column.filter);
  const hasActiveFilter = isNotNil(columnMenu.filterState[props.column.key]);
  const [activeTab, setActiveTab] = useState<ActiveTab>(!hasFilter ? 'options' : 'filter');
  return <div className="min-w-[360px] max-w-[400px]">
      {/* Tab bar — only shown when column has a filter */}
      {hasFilter && <TabsHeader variant="container" tabs={[{
      id: 'filter',
      title: 'Filter',
      icon: hasActiveFilter ? 'default' : undefined
    }, {
      id: 'options',
      title: 'Options'
    }]} activeTabId={activeTab} onChange={id => setActiveTab(id as ActiveTab)} isFullWidth />}

      {/* Tab content */}
      {activeTab === 'filter' && hasFilter && <ColumnMenuFilterTab column={props.column} filterState={columnMenu.filterState} onFilterChange={columnMenu.onFilterChange} />}
      {activeTab === 'options' && <ColumnMenuOptionsTab currentPinned={props.columnPinned} columnKey={props.column.key} columnSortable={props.column.sortable === true} onClose={props.onClose} />}
    </div>;
}